import { useState, useRef, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { MdComputer, MdPhoneAndroid } from "react-icons/md";
import { useAuth } from "../../context/AuthContext";
import { isStaff } from "../../utils/roles";

const WHATSAPP_WEB_URL =
  "https://web.whatsapp.com/send?phone=971505375791&text=Hello%20Build%20Travel%2C%20I%20need%20your%20assistance";
const WHATSAPP_MOBILE_URL =
  "https://wa.me/971505375791?text=Hello%20Build%20Travel%2C%20I%20need%20your%20assistance";

const WhatsAppButton = () => {
  const { user, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handler);
    }
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Only show for logged-in customers, not staff
  if (!user || isStaff(profile)) return null;

  return (
    <div ref={ref} className="fixed bottom-20 right-6 sm:bottom-24 sm:right-6 z-40 flex flex-col items-center gap-2">
      {/* Speed dial options — appear above the main button */}
      {open && (
        <div className="flex flex-col items-center gap-2 mb-1 animate-fadeIn">
          {/* WhatsApp Web */}
          <a
            href={WHATSAPP_WEB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 bg-white text-gray-800 border border-gray-200 px-3 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:bg-green-50 hover:border-green-200 transition-all duration-200 group min-w-[180px]"
          >
            <span className="w-9 h-9 rounded-full bg-green-100 text-green-600 flex items-center justify-center group-hover:bg-green-200 transition">
              <MdComputer size={18} />
            </span>
            <div className="flex flex-col leading-tight">
              <span className="text-xs font-semibold text-gray-700 group-hover:text-green-800">
                WhatsApp Web
              </span>
              <span className="text-[10px] text-gray-400">Open in browser</span>
            </div>
          </a>

          {/* WhatsApp Mobile */}
          <a
            href={WHATSAPP_MOBILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 bg-white text-gray-800 border border-gray-200 px-3 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:bg-green-50 hover:border-green-200 transition-all duration-200 group min-w-[180px]"
          >
            <span className="w-9 h-9 rounded-full bg-green-100 text-green-600 flex items-center justify-center group-hover:bg-green-200 transition">
              <MdPhoneAndroid size={18} />
            </span>
            <div className="flex flex-col leading-tight">
              <span className="text-xs font-semibold text-gray-700 group-hover:text-green-800">
                WhatsApp Mobile
              </span>
              <span className="text-[10px] text-gray-400">Open in app</span>
            </div>
          </a>
        </div>
      )}

      {/* Main WhatsApp FAB */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="WhatsApp support"
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
          open
            ? "bg-green-600 text-white rotate-[135deg] scale-110"
            : "bg-green-500 text-white hover:scale-105 hover:bg-green-600"
        }`}
      >
        <FaWhatsapp size={28} />
      </button>
    </div>
  );
};

export default WhatsAppButton;
